// @flow

import {
  put,
  select,
  takeLatest,
  call,
  takeEvery,
  take,
} from "redux-saga/effects";
import * as R from "ramda";

import * as atypes from "src/constants/actionTypes";
import { homescreenConversationPriority as priority } from "src/constants/priority";
import { componentPermissions } from "src/constants/roleManagement";
import * as api from "src/api/homescreen";
import getAppState, {
  getUser,
  getCurrentUserId,
  getUserMembership,
  getWorkflowTitle,
  getReadMessageCount,
  getChatroomsById,
  getUserNames,
  getRoomsByProcess,
  getAllTemplateStatus,
} from "src/selectors";
import { initialFilterState } from "src/reducers/chatRooms";
import {
  getComponentPermissionsSynced,
  getComponentPermission,
} from "src/reducers";

import {
  filterByOwner,
  filterByApproved,
  filterByMine,
  filterByUnread,
  filterByCompletion,
  filterByOverdue,
  filterByCancelled,
  filterByArchived,
} from "src/utils/filters";

import type { Action, UnifizeChatRoom } from "src/types";

function* navigateToHomescreen({ payload }: Action): any {
  try {
    const { uid } = yield select(getUser);

    if (!uid) {
      yield put({ type: atypes.SIGN_IN });
      yield put({
        type: atypes.SET_REQUESTED_PAGE,
        payload: {
          query: { id: payload.id || null },
          page: "homescreen",
        },
      });
    } else {
      yield put({
        type: atypes.SET_HOME_SCREEN_SUCCESS,
        payload,
      });
    }
  } catch (error) {
    yield put({
      type: atypes.SET_HOME_SCREEN_FAILURE,
      payload: { error },
    });
  }
}

function* watchNavigateToHomescreen(): any {
  yield takeLatest(atypes.SET_HOME_SCREEN_REQUEST, navigateToHomescreen);
}

function* fetchHomescreen(): any {
  try {
    const response = yield call(api.getHomescreen);

    yield put({
      type: atypes.GET_HOME_SCREEN_SUCCESS,
      payload: response,
    });
  } catch (error) {
    yield put({
      type: atypes.GET_HOME_SCREEN_FAILURE,
      payload: { error },
    });
  }
}

function* watchFetchHomescreen(): any {
  yield takeLatest(atypes.SET_HOME_SCREEN_SUCCESS, fetchHomescreen);
}

function* getMyConversationChartJSON({ payload }: Action): any {
  try {
    const response = yield call(api.getMyConversationChartJSON, payload.filter);

    yield put({
      type: atypes.GET_MY_CONVERSATION_CHART_SUCCESS,
      payload: { tileId: payload.tileId, metaData: response },
    });
  } catch (error) {
    yield put({
      type: atypes.GET_MY_CONVERSATION_CHART_FAILURE,
      payload: { error },
    });
  }
}

function* watchGetMyConversationChartJSON(): any {
  yield takeEvery(
    atypes.GET_MY_CONVERSATION_CHART_REQUEST,
    getMyConversationChartJSON,
  );
}

function* filterConversations({ payload }: Action): any {
  try {
    const customStatuses = payload.filter.customStatuses;

    if (R.isEmpty(payload.chatRooms)) {
      yield put({
        type: atypes.FILTER_CONVERSATIONS_SUCCESS,
        payload: {
          tileId: payload.tileId,
          filteredRooms: [],
        },
      });
      return;
    }

    const { filter, chatRooms, roomsByProcess } = payload;

    const workflowChatrooms =
      filter.workflow === null
        ? Object.values(chatRooms)
        : (roomsByProcess[filter.workflow] || [])
            .map((id) => chatRooms[id])
            .filter(Boolean);

    // $FlowFixMe - Flow doesn't know about allPass
    const rooms = R.filter(
      R.allPass([
        filterByApproved(payload.filter),
        filterByOwner(payload.filter, payload.currentUser),
        filterByCancelled(payload.filter),
        filterByOverdue(payload.filter),
        filterByCompletion(payload.filter, customStatuses),
        filterByArchived(payload.filter, payload.templateStatus),
        filterByUnread(payload.filter, payload.readMessageCount),
        filterByMine(payload.filter, payload.membership),
      ]),
      workflowChatrooms || [],
    );

    const sortDescend: Function = (sort: string) =>
      R.sort(R.descend(R.prop(sort)));
    const sortAscend: Function = (sort: string) =>
      R.sort(R.ascend(R.prop(sort)));

    const { sortBy } = payload.filter;
    let sortedRooms = [];
    switch (sortBy) {
      case "updatedAt":
        sortedRooms = sortDescend("updatedAt")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              updatedAt: new Date(
                chatroom.updatedAt ? chatroom.updatedAt : 0,
              ).getTime(),
            }),
            rooms,
          ),
        );
        break;
      case "updatedAt:ascending":
        sortedRooms = sortAscend("updatedAt")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              updatedAt: new Date(
                chatroom.updatedAt ? chatroom.updatedAt : 0,
              ).getTime(),
            }),
            rooms,
          ),
        );
        break;
      case "priority":
        sortedRooms = sortDescend("priority")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              priority:
                chatroom && chatroom.priority
                  ? priority[chatroom.priority] || 0
                  : 0,
            }),
            rooms,
          ),
        );
        break;
      case "priority:ascending":
        sortedRooms = sortAscend("priority")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              priority:
                chatroom && chatroom.priority
                  ? priority[chatroom.priority] || 0
                  : 0,
            }),
            rooms,
          ),
        );
        break;
      case "createdAt":
        sortedRooms = sortDescend("createdAt")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              createdAt: new Date(
                chatroom.createdAt ? chatroom.createdAt : "",
              ).getTime(),
            }),
            rooms,
          ),
        );
        break;
      case "createdAt:ascending":
        sortedRooms = sortAscend("createdAt")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              createdAt: new Date(
                chatroom.createdAt ? chatroom.createdAt : "",
              ).getTime(),
            }),
            rooms,
          ),
        );
        break;
      case "dueDate:ascending":
        sortedRooms = sortDescend("dueDate")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              id: chatroom.id,
              dueDate: new Date(
                chatroom.dueDate ? chatroom.dueDate : "",
              ).getTime(),
            }),
            rooms,
          ),
        );
        break;
      case "status":
        sortedRooms = sortDescend("active")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              ...chatroom,
              active: chatroom && chatroom.active ? 1 || 0 : 0,
            }),
            rooms,
          ),
        );
        break;
      case "status:ascending":
        sortedRooms = sortAscend("active")(
          R.map(
            (chatroom: UnifizeChatRoom) => ({
              ...chatroom,
              active: chatroom && chatroom.active ? 1 || 0 : 0,
            }),
            rooms,
          ),
        );
        break;
      default:
        sortedRooms = [
          ...sortAscend("dueDate")(
            R.map(
              (chatroom: UnifizeChatRoom) => ({
                id: chatroom.id,
                dueDate: new Date(
                  chatroom.dueDate ? chatroom.dueDate : "",
                ).getTime(),
              }),
              R.filter(
                (room) => room.dueDate !== null && !R.isEmpty(room.dueDate),
                rooms,
              ),
            ),
          ),
          ...R.map(
            (chatroom: UnifizeChatRoom): Object => ({ id: chatroom.id }),
            R.reject(
              (room) => room.dueDate !== null && !R.isEmpty(room.dueDate),
              rooms,
            ),
          ),
        ];
    }

    yield put({
      type: atypes.FILTER_CONVERSATIONS_SUCCESS,
      payload: {
        tileId: payload.tileId,
        filteredRooms: R.map(R.prop("id"), sortedRooms),
      },
    });
  } catch (err) {
    console.error("Failed to filter conversations: ", err);
  }
}

function* filterTileConversations({ payload }: Action): any {
  if (payload.length === 0) {
    yield put({
      type: atypes.FILTER_TILE_CONVERSATIONS_SUCCESS,
      payload: [],
    });
    return;
  }

  const membership = yield select(getUserMembership);
  const currentUser = yield select(getCurrentUserId);
  const workflows = yield select(getWorkflowTitle);
  const readMessageCount = yield select(getReadMessageCount);
  const chatRooms = yield select(getChatroomsById);
  const users = yield select(getUserNames);
  const roomsByProcess = yield select(getRoomsByProcess);
  const templateStatus = yield select(getAllTemplateStatus);

  for (const section of payload) {
    for (const tile of section.tiles) {
      yield put({
        type: atypes.FILTER_CONVERSATIONS_REQUEST,
        payload: {
          tileId: tile.id,
          filter: {
            ...initialFilterState,
            ...(tile.settings.filters || {}),
          },
          currentUser,
          workflows,
          membership,
          readMessageCount,
          chatRooms,
          users,
          roomsByProcess,
          templateStatus,
        },
      });
    }
  }

  yield put({
    type: atypes.FILTER_TILE_CONVERSATIONS_SUCCESS,
    payload: [],
  });
}

function* watchFilterTileConversations(): any {
  yield takeEvery(atypes.GET_HOME_SCREEN_SUCCESS, filterTileConversations);
}

function* watchFilterConversations(): any {
  yield takeEvery(atypes.FILTER_CONVERSATIONS_REQUEST, filterConversations);
}

function* showHomescreenReports({ payload }: Action): any {
  if (payload.id != null) {
    const componentPermissionsSynced = getComponentPermissionsSynced(
      yield select(getAppState),
    );
    if (!componentPermissionsSynced) {
      yield take(atypes.SYNC_COMPONENT_PERMISSIONS_SUCCESS);
    }

    const homeReportsAllowed = getComponentPermission(
      yield select(getAppState),
      componentPermissions.reportsTileNavigation,
    );

    if (homeReportsAllowed) {
      yield put({
        type: atypes.SET_REPORTS_REQUEST,
        payload: {
          id: payload.id,
          skipRouteAuthorization: true,
        },
      });
    }
  }
}

function* watchShowHomescreenReports(): any {
  yield takeLatest(atypes.SET_HOMESCREEN_REPORT_REQUEST, showHomescreenReports);
}

export default [
  watchNavigateToHomescreen(),
  watchFetchHomescreen(),
  watchGetMyConversationChartJSON(),
  watchFilterConversations(),
  watchFilterTileConversations(),
  watchShowHomescreenReports(),
];
